import { env } from '@/config/env';
import { useState, useRef, useEffect } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import type { Variants } from 'framer-motion';
import {
  LayoutDashboard,
  FileText,
  BarChart2,
  Users,
  // Settings,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
  // Gauge,
  // Bell,
  // Shield,
  // Plug,
  // Code,
  // LucideStar,
  KeyRound,
  CreditCard,
  BotMessageSquare,
  type LucideIcon,
} from 'lucide-react';
import { useAuth } from '@/components/auth/AuthContext';
import Button from '@shared/Button';
import T from '@/utils/i18n';

interface SidebarProps {
  isCollapsed: boolean;
  toggleSidebarCollapse: () => void;
}

interface NavItem {
  name: string;
  icon: LucideIcon;
  path: string;
  children?: NavItem[];
}

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, toggleSidebarCollapse }) => {
  const [isOpen, setIsOpen] = useState(false); // State for mobile sidebar visibility
  const [openDropdown, setOpenDropdown] = useState<string | null>(null); // State for active dropdown
  const { logout } = useAuth();
  const sidebarRef = useRef<HTMLDivElement>(null); // Ref for the scrollable sidebar content

  const handleLogout = async () => {
    await logout();
  };

  const toggleDropdown = (name: string) => {
    setOpenDropdown(openDropdown === name ? null : name);
  };

  // Effect to scroll to the opened dropdown item
  useEffect(() => {
    if (openDropdown && sidebarRef.current) {
      const dropdownItemElement = sidebarRef.current.querySelector(
        `[data-item-name="${openDropdown}"]`
      );
      if (dropdownItemElement) {
        // Increased delay to ensure the dropdown has fully rendered and transitioned before scrolling.
        // This helps in cases where the element's dimensions might not be immediately available.
        setTimeout(() => {
          dropdownItemElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 250); // Increased delay to 250ms for more reliable scrolling
      }
    }
  }, [openDropdown]);

  const navItems: NavItem[] = [
    { name: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
    { name: 'AI assistant', icon: BotMessageSquare, path: '/chat' },
    { name: 'Documents', icon: FileText, path: '/documents' },
    { name: 'Analytics', icon: BarChart2, path: '/analytics' },
    { name: 'Users', icon: Users, path: '/users' },
    // Subscription menu item - only shown in managed mode
    ...(env.isManaged
      ? [{ name: 'Subscription', icon: CreditCard, path: '/subscription/status' }]
      : []),
    ...(env.isStandalone ? [{ name: 'License', icon: KeyRound, path: '/license' }] : []),
    // License menu items - only shown in standalone mode
    // ...(env.isStandalone ? [{
    //   name: 'Licenses',
    //   icon: KeyRound,
    //   path: "/license",
    //   children: [
    //     { name: 'License Status', icon: KeyRound, path: '/license/status' },
    //     { name: 'Activate License', icon: KeyRound, path: '/license/activate' },
    //   ]
    // }] : []),
    // {
    //   name: 'Settings',
    //   icon: Settings,
    //   path: '/settings/general', // Default path for settings
    //   children: [
    //     { name: 'General', icon: LucideStar, path: '/settings/general' },
    //     ...(env.isStandalone ? [{ name: 'License', icon: KeyRound, path: '/license' }] : []),
    //     // { name: 'Users', icon: Users, path: '/settings/users' },
    //     // { name: 'Roles', icon: Shield, path: '/settings/roles' },
    //     // { name: 'Email', icon: Mail, path: '/settings/email' },
    //     // { name: 'Audit Logs', icon: ClipboardList, path: '/settings/audit-logs' },
    //     // Additional settings categories from SettingsPage.jsx
    //     { name: 'Query', icon: Settings, path: '/settings/query' },
    //     { name: 'Document Processing', icon: FileText, path: '/settings/doc-processing' },
    //     { name: 'Notifications', icon: Bell, path: '/settings/notifications' },
    //     { name: 'Security', icon: Shield, path: '/settings/security' },
    //     { name: 'Integrations', icon: Plug, path: '/settings/integrations' },
    //     { name: 'Advanced', icon: Code, path: '/settings/advanced' },
    //     { name: 'System Health', icon: Gauge, path: '/settings/system-health' },
    //   ],
    // },
  ];

  const dropdownVariants: Variants = {
    hidden: { opacity: 0, height: 0 },
    visible: { opacity: 1, height: 'auto', transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] } },
    exit: { opacity: 0, height: 0, transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] } },
  };

  return (
    <>
      {/* Mobile Sidebar Toggle Button */}
      <div className="md:hidden fixed top-4 left-4 z-50">
        <Button variant="ghost" onClick={() => setIsOpen(!isOpen)} className="p-2">
          {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </Button>
      </div>

      {/* Overlay for mobile sidebar */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black-100/50 z-40 md:hidden"
          onClick={() => setIsOpen(false)}
        ></div>
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 pt-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white p-6 flex flex-col justify-between transition-all duration-200 ease-in-out z-50
        ${isCollapsed ? 'w-25' : 'w-64'}
        ${isOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}
      >
        <Link to="/" className={`flex justify-center gap-3 mb-10 ${isCollapsed ? '' : ''}`}>
          {/*<img src="/logo.svg" alt={T.product.logo["en"]} className="h-0 w-0" />*/}
          <MessageSquare className="w-8 h-8 " />
          {!isCollapsed && <span className="text-2xl font-bold">{T.product.name['en']}</span>}
        </Link>
        {/* Added flex-grow and overflow-y-auto to make the nav scrollable */}
        <div ref={sidebarRef} className="flex-grow overflow-y-auto">
          {' '}
          {/* Attach ref here */}
          <nav>
            <ul className="space-y-2">
              {navItems.map((item) => (
                <li
                  key={item.name}
                  data-item-name={item.name}
                  className={item.children && isCollapsed ? 'relative' : ''}
                >
                  {' '}
                  {/* Added data-item-name and relative positioning for collapsed dropdown */}
                  {item.children ? (
                    <>
                      <button
                        onClick={() => toggleDropdown(item.name)}
                        className={`flex items-center w-full gap-3 py-3 rounded-lg text-gray-300 hover:bg-gray-700 hover:text-white transition-colors
                          ${openDropdown === item.name ? 'bg-primary-500/50 text-white' : ''}
                          ${isCollapsed ? 'py-3 justify-center' : 'p-3'}`}
                        title={isCollapsed ? item.name : ''}
                      >
                        <item.icon className="w-6 h-6" />
                        {!isCollapsed && <span className="text-md">{item.name}</span>}
                        {!isCollapsed && (
                          <ChevronRight
                            className={`ml-auto w-5 h-5 transition-transform ${openDropdown === item.name ? 'rotate-90' : ''}`}
                          />
                        )}
                      </button>
                      {openDropdown === item.name && (
                        <motion.ul
                          initial="hidden"
                          animate="visible"
                          exit="exit"
                          variants={dropdownVariants}
                          // Conditional styling for collapsed vs. expanded dropdown
                          // Adjusted positioning for collapsed submenu to move it further left
                          className={`mt-2 space-y-1 ${isCollapsed ? 'pl-2 max-w-10 z-50' : ' max-w-[170px] pl-6'}`} // Removed border classes
                        >
                          {item.children.map((child) => (
                            <li key={child.name}>
                              <NavLink
                                to={child.path}
                                className={({ isActive }) =>
                                  `flex items-center  p-2 rounded-lg text-gray-400 hover:bg-gray-700 hover:text-white transition-colors
                                  ${isActive ? 'bg-primary-600 text-white' : ''}
                                  ${isCollapsed ? '' : 'gap-3 p-3'}`
                                }
                                title={isCollapsed ? child.name : ''}
                                onClick={() => {
                                  setIsOpen(false); // Close sidebar on mobile after click
                                  // setOpenDropdown(null); // REMOVED: Keep parent dropdown open when navigating within its children
                                }}
                              >
                                <child.icon className={`w-5 h-5 ${isCollapsed ? '' : ''}`} />
                                <span className={`text-sm ${isCollapsed ? 'hidden' : ''}`}>
                                  {child.name}
                                </span>
                              </NavLink>
                            </li>
                          ))}
                        </motion.ul>
                      )}
                    </>
                  ) : (
                    <NavLink
                      to={item.path}
                      className={({ isActive }) =>
                        `flex items-center gap-3 py-3 rounded-lg text-gray-300 hover:bg-gray-700 hover:text-white transition-colors
                        ${isActive ? 'bg-primary-600 text-white' : ''}
                        ${isCollapsed ? 'py-3 justify-center' : 'p-3'}`
                      }
                      onClick={() => {
                        setIsOpen(false); // Close sidebar on mobile after click
                        setOpenDropdown(null); // Close any open dropdown when a non-dropdown link is clicked
                      }}
                      title={isCollapsed ? item.name : ''} // Tooltip for collapsed mode
                    >
                      <item.icon className="w-6 h-6" />
                      {!isCollapsed && <span className="text-md">{item.name}</span>}
                    </NavLink>
                  )}
                </li>
              ))}
            </ul>
          </nav>
        </div>

        <div className="mt-8">
          {/* Sidebar Toggle Button for Desktop */}
          <Button
            variant="ghost"
            onClick={toggleSidebarCollapse} // Use prop for collapse toggle
            className={`w-full items-center mb-2 py-3 rounded-lg text-white hover:bg-gray-700 hover:text-white transition-colors
            ${isCollapsed ? 'py-3' : 'flex justify-between'}`}
            title={isCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
          >
            {isCollapsed ? (
              <ChevronRight className="w-6 h-6" />
            ) : (
              <>
                <span className="text-md">Collapse</span>
                <ChevronLeft className="w-6 h-6" />
              </>
            )}
          </Button>

          <Button
            variant="ghost"
            className={`w-full  items-center mb-2 py-3 rounded-lg text-red-400 hover:bg-gray-700 hover:text-red-300
            ${isCollapsed ? 'py-3' : 'flex justify-between'}`}
            onClick={handleLogout}
            title={isCollapsed ? 'Log Out' : ''} // Tooltip for collapsed mode
          >
            {!isCollapsed && <span className="text-md">Log Out</span>}
            <LogOut className="w-6 h-6" />
          </Button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
